import { useEffect, type ReactNode } from "react";
import { NinjaArt } from "../art/NinjaArt";
import { ModePicker } from "../components/ModePicker";
import { SetDots } from "../components/SetDots";
import { Stripes } from "../components/Stripes";
import { MODE_INFO, type Mode, type Op } from "../content/modes";
import { ninjaOfRank, type Ninja } from "../content/ninjas";
import { RANKS } from "../content/ranks";
import { variantName } from "../content/variants";
import { trialCoins } from "../game/coins";
import { kitNames, type Kit } from "../game/items";
import { availableModes } from "../game/modes";
import { isKnown } from "../game/roster";
import { goldPossible, setModes, setProgress } from "../game/sets";
import { cardStripes } from "../game/stripes";
import { nextTrial, trialLock, type TrialLock } from "../game/trials";
import type { SaveState } from "../save/schema";
import { speak, stopSpeaking } from "../speech/speech";
import "./Staging.css";

/*
  The front doors (Phase 4.5). Each way to play gets a short screen between
  Home and the round: what you're about to do, what you get, and one huge
  Start. The strand picker lives here, not on Home. Read aloud when read-aloud
  is on, so the 4-year-old gets the same words as the 7-year-old.
*/

/** "Take, Times and Share" */
export function listOf(words: readonly string[]): string {
  if (words.length <= 1) return words[0] ?? "";
  return `${words.slice(0, -1).join(", ")} and ${words[words.length - 1]}`;
}

/** "Swift Star in Add needs Take, Times and Share at Shadow Cub first." */
export function lockText(mode: Mode, targetRank: number, lock: TrialLock): string {
  const names = lock.behind.map((op) => MODE_INFO[op].label);
  return `${RANKS[targetRank]!.name} in ${MODE_INFO[mode].label} needs ${listOf(names)} at ${RANKS[lock.rankIndex]!.name} first.`;
}

export const TRIAL_TIPS = [
  "Wrong answers don't lose points.",
  "Stuck? Tap Pass.",
  "Stripes on the way up.",
] as const;
export const BATTLE_TIPS = [
  "Right answers hit. Harder questions hit harder.",
  "Wrong answers hurt you.",
  "Beat a ninja and it joins your team.",
] as const;
export const PRACTICE_TIP = "Practice is untimed and nothing counts against you.";

interface FrameProps {
  title: string;
  /** The hero: a card or a ninja. */
  hero: ReactNode;
  /** What's read aloud when read-aloud is on; changes re-read. */
  say: string;
  audio: boolean;
  tips: readonly string[];
  /** The big button; none when there's nothing to start (the top rank, a lock). */
  start?: { label: string; onClick: () => void; className?: string } | undefined;
  onBack: () => void;
  children?: ReactNode;
}

function StagingFrame({ title, hero, say, audio, tips, start, onBack, children }: FrameProps) {
  useEffect(() => {
    if (audio) speak(say);
    return stopSpeaking;
  }, [audio, say]);
  return (
    <section className="screen staging">
      <header className="staging-head">
        <button type="button" className="ghost" onClick={onBack}>
          Back
        </button>
        <h2 className="display staging-title">{title}</h2>
      </header>
      <div className="staging-body">
        <div className="staging-hero">{hero}</div>
        <div className="staging-side">
          {children}
          <ul className="tips">
            {tips.map((t) => (
              <li key={t}>{t}</li>
            ))}
          </ul>
          {start && (
            <button
              type="button"
              className={"big display start-btn " + (start.className ?? "")}
              onClick={start.onClick}
            >
              {start.label}
            </button>
          )}
        </div>
      </div>
    </section>
  );
}

// ─── Trials ─────────────────────────────────────────────────────────────────

interface TrialProps {
  save: SaveState;
  mode: Mode;
  onSelectMode: (mode: Mode) => void;
  onStart: () => void;
  onBack: () => void;
  onOpenCard: (rankIndex: number, mode: Mode) => void;
}

/** What the trial pays and needs, for the screen and for read-aloud. */
export function trialLines(save: SaveState, mode: Mode): string[] {
  const spec = nextTrial(mode, save.rank[mode]);
  if (!spec) return [`You're a Legend in ${MODE_INFO[mode].label}!`];
  const first = !save.trials[mode]?.[String(spec.rankIndex)]?.passed;
  const coins = trialCoins(spec.rankIndex, first);
  const lines = [
    `${spec.passMark} right in 3 minutes.`,
    `Stripes at ${listOf(spec.stripes.map(String))}.`,
  ];
  if (coins) lines.push(`First pass: ${coins} coins.`);
  return lines;
}

/** "Pass this and Shadow Cub goes gold!" when this strand is the last one. */
export function goldLine(save: SaveState, mode: Mode, rankIndex: number): string | null {
  const ops = save.settings.ops;
  if (!goldPossible(ops)) return null;
  const others = setModes(ops).filter((m) => m !== mode);
  if (!others.every((m) => save.rank[m] >= rankIndex)) return null;
  return `Pass this and ${RANKS[rankIndex]!.name} goes gold!`;
}

export function TrialStaging({
  save,
  mode,
  onSelectMode,
  onStart,
  onBack,
  onOpenCard,
}: TrialProps) {
  const modes = availableModes(save.settings.ops);
  const rank = save.rank[mode];
  const spec = nextTrial(mode, rank);
  const lock = trialLock(save.rank, save.settings.ops, mode);
  const held = RANKS[rank]!;
  const stripes = cardStripes(save.stripes, mode, rank);
  const label = MODE_INFO[mode].label;
  const target = spec ? RANKS[spec.rankIndex]! : null;
  const known = spec ? isKnown(save.rank, save.roster, ninjaOfRank(spec.rankIndex)) : true;
  const targetName = target ? (known ? variantName(mode, target.name) : "???") : "";
  const lines = trialLines(save, mode);
  const gold = spec ? goldLine(save, mode, spec.rankIndex) : null;
  const dots =
    spec && goldPossible(save.settings.ops)
      ? setProgress(save.rank, save.settings.ops, spec.rankIndex)
      : null;
  const locked = spec && lock ? lockText(mode, spec.rankIndex, lock) : null;

  const say = [
    `${label} trial.`,
    locked ??
      (spec ? `Next: ${known ? target!.name : "a mystery ninja"}. ${lines.join(" ")}` : lines[0]!),
    gold ?? "",
    ...(locked ? [] : TRIAL_TIPS),
  ]
    .filter(Boolean)
    .join(" ");

  const hero = spec ? (
    <button
      type="button"
      className={"staging-card" + (locked ? " locked" : "")}
      style={{ "--c": target!.colour } as React.CSSProperties}
      onClick={() => onOpenCard(spec.rankIndex, mode)}
      aria-label={`Next: ${targetName}, pass mark ${spec.passMark}${locked ? ", locked" : ""}. See the card.`}
    >
      <span className="staging-kicker">Next rank</span>
      <NinjaArt rank={target!} variant={mode} locked={!known || !!locked} className="staging-art" />
      <span className="staging-name display">{targetName}</span>
      {locked && (
        <span className="lock-tag" aria-hidden="true">
          Locked
        </span>
      )}
      {dots && <SetDots dots={dots} className="staging-dots" />}
    </button>
  ) : (
    <div className="staging-card" style={{ "--c": held.colour } as React.CSSProperties}>
      <span className="staging-kicker">{label}</span>
      <NinjaArt rank={held} variant={mode} className="staging-art" />
      <span className="staging-name display">{variantName(mode, held.name)}</span>
    </div>
  );

  return (
    <StagingFrame
      title="Trials"
      hero={hero}
      say={say}
      audio={save.settings.audio}
      tips={locked ? [] : TRIAL_TIPS}
      start={spec && !locked ? { label: "Start trial", onClick: onStart } : undefined}
      onBack={onBack}
    >
      <ModePicker modes={modes} selected={mode} onSelect={onSelectMode} />
      <button
        type="button"
        className="staging-now"
        onClick={() => onOpenCard(rank, mode)}
        aria-label={`${label}: ${variantName(mode, held.name)}${spec ? `, ${stripes} of 3 stripes` : ""}. See the card.`}
      >
        You&apos;re <b>{variantName(mode, held.name)}</b> in {label}
        {spec && (
          <>
            {" "}
            <Stripes count={stripes} />
          </>
        )}
      </button>
      {locked && lock ? (
        <div className="lock-note" role="status">
          <p>{locked}</p>
          <div className="row">
            {lock.behind.map((op: Op) => (
              <button key={op} type="button" className="mini" onClick={() => onSelectMode(op)}>
                {MODE_INFO[op].label} trial
              </button>
            ))}
          </div>
        </div>
      ) : (
        <ul className="staging-lines">
          {lines.map((l) => (
            <li key={l}>{l}</li>
          ))}
          {gold && <li className="gold-line">{gold}</li>}
        </ul>
      )}
    </StagingFrame>
  );
}

// ─── Practice ───────────────────────────────────────────────────────────────

interface PracticeProps {
  save: SaveState;
  mode: Mode;
  onSelectMode: (mode: Mode) => void;
  onStart: () => void;
  onBack: () => void;
}

export function practiceLength(minutes: number): string {
  if (!minutes) return "No clock. Tap Done when you've had enough.";
  return `${minutes} minute${minutes === 1 ? "" : "s"} on the clock.`;
}

export function PracticeStaging({ save, mode, onSelectMode, onStart, onBack }: PracticeProps) {
  const modes = availableModes(save.settings.ops);
  const held = RANKS[save.rank[mode]]!;
  const label = MODE_INFO[mode].label;
  const length = practiceLength(save.settings.practice.minutes);
  const lines = [length, "A coin for every 2 right answers."];
  const say = `${label} practice. ${PRACTICE_TIP} ${lines.join(" ")}`;
  return (
    <StagingFrame
      title="Practice"
      hero={
        <div className="staging-card" style={{ "--c": held.colour } as React.CSSProperties}>
          <span className="staging-kicker">{label}</span>
          <NinjaArt rank={held} variant={mode} className="staging-art" />
          <span className="staging-name display">{variantName(mode, held.name)}</span>
        </div>
      }
      say={say}
      audio={save.settings.audio}
      tips={[PRACTICE_TIP]}
      start={{ label: "Start practice", onClick: onStart, className: "practice" }}
      onBack={onBack}
    >
      <ModePicker modes={modes} selected={mode} onSelect={onSelectMode} />
      <ul className="staging-lines">
        {lines.map((l) => (
          <li key={l}>{l}</li>
        ))}
      </ul>
    </StagingFrame>
  );
}

// ─── Battle ─────────────────────────────────────────────────────────────────

interface BattleProps {
  save: SaveState;
  mode: Mode;
  enemy: Ninja;
  fighter: Ninja;
  kit: Kit;
  onSelectMode: (mode: Mode) => void;
  onStart: () => void;
  onRoster: () => void;
  onBack: () => void;
}

export function BattleStaging({
  save,
  mode,
  enemy,
  fighter,
  kit,
  onSelectMode,
  onStart,
  onRoster,
  onBack,
}: BattleProps) {
  const modes = availableModes(save.settings.ops);
  const known = isKnown(save.rank, save.roster, enemy);
  const enemyRank = RANKS[enemy.rankIndex]!;
  const name = known ? enemy.name : "???";
  const carried = kit.active.length + kit.passive.length;
  const empty = fighter.slots.active + fighter.slots.passive - carried;
  const owned = Object.keys(save.items).length;
  // An empty slot is a nudge once the child owns something to fit, as Home said before.
  const wearing = carried
    ? `, with ${kitNames(kit)}`
    : owned && empty
      ? `, with ${empty} empty slot${empty === 1 ? "" : "s"}`
      : "";
  const lines = [
    `${known ? enemy.name : "A mystery ninja"}: ${enemy.health} health.`,
    `Fighting with ${fighter.name}${wearing}.`,
  ];
  const say = `Battle. ${lines.join(" ")} ${BATTLE_TIPS.join(" ")}`;
  return (
    <StagingFrame
      title="Battle"
      hero={
        <div
          className="staging-card foe"
          style={{ "--c": enemyRank.colour } as React.CSSProperties}
        >
          <span className="staging-kicker">Your opponent</span>
          <NinjaArt rank={enemyRank} locked={!known} className="staging-art" />
          <span className="staging-name display">{name}</span>
          <span className="staging-sub">{enemy.health} health</span>
        </div>
      }
      say={say}
      audio={save.settings.audio}
      tips={BATTLE_TIPS}
      start={{ label: "Start battle", onClick: onStart, className: "battle-btn" }}
      onBack={onBack}
    >
      <ModePicker modes={modes} selected={mode} onSelect={onSelectMode} />
      <ul className="staging-lines">
        {lines.map((l) => (
          <li key={l}>{l}</li>
        ))}
      </ul>
      <button type="button" className="mini" onClick={onRoster}>
        Change ninja or items
      </button>
    </StagingFrame>
  );
}
